/**
 * Topbar — barre supérieure de l'app ERP authentifiée.
 *
 * Composition standard : nom commercial + nom du domaine, recherche globale
 * (raccourci Ctrl+K), cloche de notifications, avatar utilisateur cliquable.
 *
 * La recherche ouvre la palette globale `CommandPalette` (Lot H.5), montée au
 * niveau du shell : le bouton reçoit ici `onOpenSearch` + `searchButtonRef`
 * (ce dernier sert à rendre le focus au bouton quand la palette se ferme).
 */

import { Bell, Menu, Search } from "lucide-react";
import { type Ref } from "react";

import { TenantSwitcher, type TenantSwitcherTenant } from "./TenantSwitcher.js";
import { UserMenu } from "./UserMenu.js";

export interface TopbarProps {
  /** Nom commercial affiché à gauche (défaut Crayéa). */
  brandName?: string;
  /** Liste des tenants accessibles à l'utilisateur (1 → texte simple, 2+ → switcher). */
  tenants: ReadonlyArray<TenantSwitcherTenant>;
  /** ID du tenant actif (sert à mettre l'item sélectionné en évidence). */
  activeTenantId: string | null;
  /** Données utilisateur affichées sur l'avatar (+ menu déroulant). */
  utilisateur: {
    initiales: string;
    nomComplet: string;
    role: string;
  };
  /** Nombre de notifications non lues (badge gold sur la cloche). */
  notificationsCount?: number;
  /** Ouvre la palette de recherche globale (Ctrl+K). */
  onOpenSearch?: () => void;
  /** Ref sur le bouton recherche : la palette lui rend le focus à la fermeture. */
  searchButtonRef?: Ref<HTMLButtonElement>;
  /** Ouvre le tiroir de navigation (bouton visible sous `lg` uniquement). */
  onOuvrirMenu?: () => void;
}

export function Topbar({
  brandName = "Crayéa",
  tenants,
  activeTenantId,
  utilisateur,
  notificationsCount = 0,
  onOpenSearch,
  searchButtonRef,
  onOuvrirMenu,
}: TopbarProps) {
  return (
    <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-champagne-900/10 bg-champagne-50 px-4 lg:px-8">
      <div className="flex min-w-0 flex-1 items-center gap-2 lg:gap-3">
        {/* Ouverture du tiroir de navigation — la barre latérale n'existe dans
            le flux qu'à partir de `lg`. 48 px de côté : cible tactile
            confortable, y compris avec les doigts froids ou humides. */}
        <button
          type="button"
          onClick={onOuvrirMenu}
          aria-label="Ouvrir le menu de navigation"
          className="ui-btn-outline inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl lg:hidden"
        >
          <Menu size={22} className="text-champagne-800" aria-hidden="true" />
        </button>
        {/* Marque masquée sous `lg` : elle entrait en concurrence avec le nom
            du domaine pour ~110 px utiles et les deux finissaient rognés
            (« C.. » / « · Champagne Lefè… »). Le nom du DOMAINE prime — c'est
            l'information qui change d'un tenant à l'autre. La marque reste
            visible en tête du tiroir de navigation. */}
        <div className="hidden shrink-0 text-[15px] font-semibold tracking-tight lg:block">
          {brandName}
        </div>
        <TenantSwitcher tenants={tenants} activeId={activeTenantId} />
      </div>

      <div className="flex shrink-0 items-center gap-2 max-lg:[&_button]:min-h-12 lg:gap-3">
        {/* Recherche globale (raccourci Ctrl+K) → ouvre la palette CommandPalette.
            Cible RM française = quasi 100% Windows : on affiche `Ctrl+K`
            en dur plutôt que le `⌘K` (Cmd) macOS qui n'évoque rien ici.
            Le badge du raccourci est masqué sous `lg` : au doigt il n'a aucun
            sens et il vole de la place sur une barre déjà chargée. */}
        <button
          ref={searchButtonRef}
          type="button"
          onClick={onOpenSearch}
          className="ui-btn-outline inline-flex h-12 min-w-12 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-[13px] lg:h-10 lg:px-3.5"
          aria-label="Rechercher (Ctrl+K)"
        >
          <Search size={16} aria-hidden="true" />
          <span className="hidden lg:inline">Rechercher</span>
          <span className="mono ml-1 hidden rounded bg-champagne-900/[0.06] px-1.5 py-0.5 text-[11px] text-champagne-700 lg:inline">
            Ctrl+K
          </span>
        </button>

        {/* Notifications avec badge gold.
            Masquée sous `lg` tant qu'aucune notification n'est à montrer : le
            module n'existe pas encore (compteur figé à 0, cf. TODO V1), et ces
            44 px manquaient au nom du domaine, qui s'affichait « · Champagne
            Lefè… » sur toutes les pages. Dès que le compteur sera réel, la
            cloche réapparaîtra d'elle-même en mobile. */}
        <button
          type="button"
          aria-label={`Notifications${notificationsCount > 0 ? ` (${notificationsCount} non lues)` : ""}`}
          className={`ui-btn-outline relative inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl lg:h-10 lg:w-10 ${
            notificationsCount > 0 ? "" : "hidden lg:inline-flex"
          }`}
        >
          <Bell size={22} className="text-champagne-800" aria-hidden="true" />
          {notificationsCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full border-[1.5px] border-champagne-50 bg-champagne-600 px-1 text-[10px] font-bold text-white">
              {notificationsCount}
            </span>
          )}
        </button>

        {/* Avatar + dropdown (Se déconnecter, à enrichir plus tard avec
            paramètres compte / changer de domaine). */}
        <UserMenu utilisateur={utilisateur} />
      </div>
    </header>
  );
}
